import React from 'react';
import type { FormInstance } from 'antd';
import { Form } from 'antd';
import BoldOutlined from '@ant-design/icons/BoldOutlined';
import ItalicOutlined from '@ant-design/icons/ItalicOutlined';
import UnderlineOutlined from '@ant-design/icons/UnderlineOutlined';
import { RibbonIconButton } from '../bookDefaultStyles/WordStyleRibbon';
import { FormToggleIconButton } from './FormToggleIconButton';
import { styleFieldName } from '../../utils/styleFormFieldNames';
import { isDedicatedFontFamilyBase } from '../../utils/fontFamily';
import { applyDedicatedTypographyToForm } from './fontFamilyFormSync';

type Props = {
  form: FormInstance;
  namePrefix?: string | string[];
};

export const FontWeightToggleButtons: React.FC<Props> = ({ form, namePrefix }) => {
  const fontWeight = Form.useWatch(styleFieldName(namePrefix, 'fontWeight'), form);
  const bold = Form.useWatch(styleFieldName(namePrefix, 'bold'), form);
  const fontFamily = Form.useWatch(styleFieldName(namePrefix, 'fontFamily'), form);

  const isDedicated = isDedicatedFontFamilyBase(fontFamily);
  const weight = Number(fontWeight);
  const hasWeight = fontWeight != null && fontWeight !== '' && Number.isFinite(weight);
  const isBold = isDedicated
    ? Boolean(bold)
    : Boolean(bold) || (hasWeight && weight >= 600);

  return (
    <>
      <Form.Item name={styleFieldName(namePrefix, 'bold')} hidden initialValue={false}>
        <input type="hidden" />
      </Form.Item>
      <div className="word-ribbon-inline">
        <RibbonIconButton
          title="Normal"
          active={!isBold}
          className="text-btn"
          onClick={() => {
            form.setFieldValue(styleFieldName(namePrefix, 'bold'), false);
            if (applyDedicatedTypographyToForm(form, namePrefix)) return;
            form.setFieldValue(styleFieldName(namePrefix, 'fontWeight'), 400);
          }}
        >
          N
        </RibbonIconButton>
        <RibbonIconButton
          title="Bold"
          active={isBold}
          className={isBold ? 'word-ribbon-icon-btn--bold' : undefined}
          onClick={() => {
            form.setFieldValue(styleFieldName(namePrefix, 'bold'), true);
            if (applyDedicatedTypographyToForm(form, namePrefix)) return;
            form.setFieldValue(styleFieldName(namePrefix, 'fontWeight'), 700);
          }}
        >
          <BoldOutlined />
        </RibbonIconButton>
      </div>
    </>
  );
};

const HeaderItalicToggle: React.FC<{
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  form: FormInstance;
  namePrefix?: string | string[];
}> = ({ checked, onChange, form, namePrefix }) => (
  <FormToggleIconButton
    title="Italic"
    effect="italic"
    className="italic-btn"
    checked={checked}
    onChange={(next) => {
      onChange?.(next);
      applyDedicatedTypographyToForm(form, namePrefix);
    }}
  >
    <ItalicOutlined />
  </FormToggleIconButton>
);

export const ItalicUnderlineToggles: React.FC<Props> = ({ form, namePrefix }) => (
  <div className="word-ribbon-inline">
    <Form.Item
      name={styleFieldName(namePrefix, 'italic')}
      valuePropName="checked"
      initialValue={false}
      noStyle
    >
      <HeaderItalicToggle form={form} namePrefix={namePrefix} />
    </Form.Item>
    <Form.Item
      name={styleFieldName(namePrefix, 'underline')}
      valuePropName="checked"
      initialValue={false}
      noStyle
    >
      <FormToggleIconButton title="Underline" effect="underline">
        <UnderlineOutlined />
      </FormToggleIconButton>
    </Form.Item>
  </div>
);
